import {
  allAccessCardOptions,
  companiesNamesOptions,
  getUserNamesOptions,
  getUsersByIdsOptions,
  useAllAccessCards,
  useBuilding,
  useFrozenCardsForBuilding,
  userOptions,
} from "@/api";
import AccessCardDetailsModal from "@/components/access_cards/AccessCardDetailsModal";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import Table from "@/components/table/Table";
import type { Table as ReactTable } from "@tanstack/react-table";
import { useWelcomeAccessAccessForBuilding } from "@/hooks/useWelcomeAccessAccess";
import i18n from "@/i18n/config";
import { accessCardStatusColorMap, accessCardStatusColorMapNew } from "@/lib";
import {
  dateLikeSortingFn,
  dateToFirebaseTimestamp,
  debounce,
  formatTimestampToTimeAgo,
  getSelectedItemsInDisplayOrder,
  timestampSortingFn,
} from "@/lib";
import {
  AccessBadge,
  AccessCardOrderType,
  CardType,
  CardTypeNew,
  Company,
} from "@/types";
import {
  Button,
  Checkbox,
  Icon,
  InputField,
  LoadingSpinner,
  PopupCard,
  SelectRow,
} from "@app-components";
import { useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import { RowSelectionState } from "@tanstack/react-table";
import { AnimatePresence, motion } from "framer-motion";
import { PrintAccessCard } from "@/components/badge_printing/PrintAccessCard";
import { useSessionState } from "@/hooks/useSessionState";
import styles from "@/styles/AccessCards.module.scss";
import { FrozenCardDetailsModal } from "@/components/access_cards/FrozenCardDetailsModal";
import { useBuildingQuery } from "@/components/RouterSetup";
import { AcsConnectivityBanner } from "@/components/AcsConnectivityBanner";
import { useAccessAdminPermission } from "@/hooks/useAccessAdminPermission";

const searchableColumns: Array<string> = [
  "cardNumber",
  "orderedBy.company",
  "orderedBy.name",
  "userName",
];

const frozenCardSearchableColumns: Array<string> = [
  "cardNumber",
  "userName",
  "companyName",
];

export const Route = createFileRoute("/_portal/building/access")({
  component: AccessCards,
});

type AccessTableRow = AccessCardOrderType & selectType;
type FrozenCardTableRow = AccessBadge & {
  userName?: string;
  companyName?: string;
};

type selectType = {
  selectCol?: string;
};

const columnHelper = createColumnHelper<AccessTableRow>();
const frozenColumnHelper = createColumnHelper<FrozenCardTableRow>();

function AccessCards() {
  const { search } = Route.useSearch() as { search?: string };
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filteredRequests, setFilteredRequests] = useState<
    AccessCardOrderType[]
  >([]);
  const [filteredCards, setFilteredCards] = useState<AccessCardOrderType[]>([]);
  const [filteredFrozenCards, setFilteredFrozenCards] = useState<
    FrozenCardTableRow[]
  >([]);
  const [searchTerm, setSearchTerm] = useState(search || "");
  const [selectedCard, setSelectedCard] = useState<AccessCardOrderType>();
  const [selectedFrozenCard, setSelectedFrozenCard] =
    useState<AccessBadge>();
  const [modalOpen, setModalOpen] = useState(false);
  const [frozenCardModalOpen, setFrozenCardModalOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [printCardsOpen, setPrintCardsOpen] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [tableInstance, setTableInstance] =
    useState<ReactTable<AccessTableRow> | null>(null);
  const [tab, setTab] = useSessionState<string>(
    "accessCardRequests",
    "requests",
  );

  const { data: building } = useBuilding();
  const { isBuildingAccessAdmin } = useAccessAdminPermission({
    withoutRedirect: false,
  });
  const { buildingIntegratedWithAccessControlSystem } =
    useWelcomeAccessAccessForBuilding();

  const { isLoading: isLoadingAllCards, data: allCards } = useBuildingQuery<
    AccessCardOrderType[]
  >(
    allAccessCardOptions(
      isBuildingAccessAdmin,
      false,
      building?.disablePlasticCardOrdering,
    ),
  );

  const { data: cardRequests } = useAllAccessCards(
    isBuildingAccessAdmin,
    true,
    building?.disablePlasticCardOrdering,
  );
  const { data: selectedUsers, isLoading: isLoadingSelectedUsers } =
    useBuildingQuery({
      ...getUsersByIdsOptions(
        selectedUserIds,
        printCardsOpen && selectedUserIds.length > 0,
      ),
    });
  const { data: frozenCards, isLoading: isLoadingFrozenCards } =
    useFrozenCardsForBuilding(
      buildingIntegratedWithAccessControlSystem,
      isBuildingAccessAdmin,
    );

  const allCardsWithRequests = [...(allCards || []), ...(cardRequests || [])];
  const cardUserIdsSet = new Set<string>();
  allCardsWithRequests.forEach((card) => {
    if (card.userId) cardUserIdsSet.add(card.userId);
    if (card.orderedBy?.userId) cardUserIdsSet.add(card.orderedBy.userId);
  });

  (frozenCards || [])?.forEach((card) => {
    if (card.userId) cardUserIdsSet.add(card.userId);
  });

  const uniqueCardUserIds = Array.from(cardUserIdsSet);

  const { data: newUserNames } = useBuildingQuery({
    ...getUserNamesOptions(isBuildingAccessAdmin, uniqueCardUserIds),
    enabled: isBuildingAccessAdmin,
  });

  // Get unique company IDs from frozen cards
  const uniqueCompanyIds = useMemo(() => {
    if (!frozenCards) return [];
    return Array.from(
      new Set(
        frozenCards
          .map((card) => card.companyId)
          .filter(
            (id): id is string => typeof id === "string" && id.length > 0,
          ),
      ),
    );
  }, [frozenCards]);

  const { data: companyNames } = useBuildingQuery(
    companiesNamesOptions(uniqueCompanyIds, { noExternal: true }),
  );

  const selectedCardOrdersCount = Object.keys(rowSelection).length;

  const tabs = useMemo(
    () => [
      {
        value: "requests",
        label: t("ACCESS_CARD.TABS.REQUESTS"),
      },
      {
        value: "completed",
        label: t("ACCESS_CARD.TABS.COMPLETED"),
      },
    ],
    [t],
  );

  useEffect(() => {
    if (newUserNames) {
      setUserNames((prev) => ({ ...prev, ...newUserNames }));
    }
  }, [newUserNames]);

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash) {
      setTab(hash === "completed" ? "completed" : "requests");
    }
  }, [location.hash, setTab]);

  useEffect(() => {
    const processedCards = processCardsWithUserNames(allCards || [], userNames);
    const processedRequests = processCardsWithUserNames(
      cardRequests || [],
      userNames,
    );
    const processedFrozenCards = processFrozenCardsWithNames(
      frozenCards || [],
      userNames,
      companyNames,
    );

    setFilteredCards(filterCardsBySearchTerm(processedCards, searchTerm));
    setFilteredRequests(filterCardsBySearchTerm(processedRequests, searchTerm));
    setFilteredFrozenCards(
      filterFrozenCardsBySearchTerm(processedFrozenCards, searchTerm),
    );
  }, [
    searchTerm,
    cardRequests,
    allCards,
    userNames,
    companyNames,
    frozenCards,
  ]);

  const processCardsWithUserNames = (
    cards: AccessCardOrderType[],
    userNames?: Record<string, string>,
  ) => {
    if (!userNames) return cards;

    return cards.map((card) => ({
      ...card,
      userName: userNames[card.userId],
      orderedBy: {
        ...card.orderedBy,
        name: userNames[card.orderedBy.userId],
      },
    }));
  };

  const processFrozenCardsWithNames = (
    cards: AccessBadge[],
    userNames?: Record<string, string>,
    companyNames?: Partial<Company>[],
  ): FrozenCardTableRow[] => {
    return cards.map((card) => ({
      ...card,
      userName: userNames?.[card.userId || ""] || "\u2014",
      companyName:
        companyNames?.find((c) => c.buildingTenantId === card.companyId)
          ?.buildingTenantName || "\u2014",
    }));
  };

  const filterCardsBySearchTerm = (
    cards: AccessCardOrderType[],
    searchTerm: string,
  ) => {
    if (!searchTerm) return cards;

    return cards.filter((card) =>
      searchableColumns.some((column) => {
        const val = card[column as keyof AccessCardOrderType];
        return val
          ? (val?.toString().toLowerCase() || "").startsWith(
              searchTerm.toLowerCase(),
            )
          : false;
      }),
    );
  };

  const filterFrozenCardsBySearchTerm = (
    cards: FrozenCardTableRow[],
    searchTerm: string,
  ) => {
    if (!searchTerm) return cards;

    return cards.filter((card) =>
      frozenCardSearchableColumns.some((column) => {
        const val = card[column as keyof FrozenCardTableRow];
        return val
          ? (val?.toString().toLowerCase() || "").startsWith(
              searchTerm.toLowerCase(),
            )
          : false;
      }),
    );
  };

  const handleCardPrinting = () => {
    const selectedCards = getSelectedItemsInDisplayOrder(tableInstance);
    const userIds = selectedCards.map((card) => card.userId).filter(Boolean);
    setSelectedUserIds(userIds);
    setPrintCardsOpen(true);
  };

  const handleTabChange = (newTab: string) => {
    navigate({ hash: newTab });
  };

  const handleRowClick = (card: AccessCardOrderType) => {
    setSelectedCard(card);
    setModalOpen(true);
  };

  const handleFrozenCardRowClick = (card: AccessBadge) => {
    setSelectedFrozenCard(card);
    setFrozenCardModalOpen(true);
  };

  const handleFrozenCardRowHover = useMemo(
    () =>
      debounce((card: AccessBadge) => {
        if (!card.userId) return;
        queryClient.ensureQueryData(userOptions(card.userId, card.companyId));
      }, 200),
    [queryClient],
  );

  const requestColumns = useMemo(
    () => createBaseColumns(true, building?.allowBadgePrinting, t),
    [tab, building?.allowBadgePrinting, t],
  );

  const restColumns = useMemo(
    () => createBaseColumns(false, building?.allowBadgePrinting, t),
    [tab, building?.allowBadgePrinting, t],
  );

  const frozenCardColumns = useMemo(() => createFrozenCardColumns(t), [t]);

  if (!isBuildingAccessAdmin) {
    return null;
  }

  const isRequestsTab = tab === "requests";

  if (building?.disablePlasticCardOrdering) {
    return (
      <>
        <div className="pageHeader">
          <h1>{t("PAGES.BUILDING_ACCESS.HEADER")}</h1>
          <p>{t("PAGES.BUILDING_ACCESS.SUBHEADER")}</p>
        </div>
        <div className={styles.searchContainer} style={{ width: "350px" }}>
          <InputField
            label={t("ACCESS_CARD.SEARCH")}
            placeholder={t("ACCESS_CARD.SEARCH") + "..."}
            icon="search"
            value={searchTerm}
            compact
            hideLabel
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className={styles.frozenCardsSection}>
          <h2 className={`${styles.tableHeader} ${styles.frozenCards}`}>
            {t("ACCESS_CARD.TABLE_HEADERS.FROZEN_CARDS")}{" "}
            <span className={styles.count}>
              {frozenCards?.length || 0} {t("TOTAL")}
              {searchTerm &&
                t("ACCESS_CARD.HEADER_COUNT_SEARCH", {
                  count: filteredFrozenCards.length,
                })}
            </span>
          </h2>
          <Table<FrozenCardTableRow>
            key="frozenCards"
            name="frozenCards"
            data={filteredFrozenCards}
            columns={frozenCardColumns}
            isLoading={isLoadingFrozenCards}
            noDataMessage={t("ACCESS_CARD.NO_FROZEN_CARDS")}
            onRowHover={handleFrozenCardRowHover}
            onRowClick={handleFrozenCardRowClick}
            enableColumnControls
            defaultSorting={[
              {
                id: "updatedAt",
                desc: true,
              },
            ]}
          />
        </div>
        {selectedFrozenCard && (
          <FrozenCardDetailsModal
            open={frozenCardModalOpen}
            accessCard={selectedFrozenCard}
            onClose={() => setFrozenCardModalOpen(false)}
            userCompanyName={
              companyNames?.find(
                (c) => c.buildingTenantId === selectedFrozenCard.companyId,
              )?.buildingTenantName || "\u2014"
            }
          />
        )}
      </>
    );
  }

  return (
    <>
      <AcsConnectivityBanner />
      <div className="pageHeader">
        <h1>{t("PAGES.BUILDING_ACCESS.HEADER")}</h1>
        <p>{t("PAGES.BUILDING_ACCESS.SUBHEADER")}</p>
      </div>
      <div className={styles.controlsContainer}>
        <div className={styles.leftControls}>
          <div className={styles.tabsContainer}>
            <SelectRow
              tabs
              label="Tabs"
              hideLabel
              name="building-access-tabs"
              options={tabs}
              value={tab}
              onChange={() =>
                handleTabChange(tab === "requests" ? "completed" : "requests")
              }
            />
          </div>
          <div className={styles.searchContainer}>
            <InputField
              label={t("ACCESS_CARD.SEARCH")}
              placeholder={t("ACCESS_CARD.SEARCH") + "..."}
              icon="search"
              value={searchTerm}
              compact
              hideLabel
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className={styles.printButtonContainer}>
          <AnimatePresence>
            {selectedCardOrdersCount > 0 && isRequestsTab && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
              >
                <Button icon="print" onClick={handleCardPrinting}>
                  {t("BADGE_PRINTING.PRINT.PRINT_CARD", {
                    count: selectedCardOrdersCount,
                  })}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {tab === "requests" ? (
        <>
          <h2 className={styles.tableHeader}>
            {t("ACCESS_CARD.REQUESTS_HEADER")}{" "}
            <span className={styles.count}>
              {cardRequests.length} {t("TOTAL")}
              {searchTerm &&
                t("ACCESS_CARD.HEADER_COUNT_SEARCH", {
                  count: filteredRequests.length,
                })}
            </span>
          </h2>
          <Table<AccessTableRow>
            key="cardOrders"
            name="cardOrders"
            data={filteredRequests}
            columns={requestColumns}
            onRowClick={handleRowClick}
            onTableReady={setTableInstance}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            noDataMessage={t("PAGES.BUILDING_ACCESS.REQUESTS_PLACEHOLDER")}
            enableColumnControls
            defaultSorting={[
              {
                id: "createdAt",
                desc: true,
              },
            ]}
          />

          <div className={styles.frozenCardsSection}>
            <h2 className={`${styles.tableHeader} ${styles.frozenCards}`}>
              {t("ACCESS_CARD.TABLE_HEADERS.FROZEN_CARDS")}{" "}
              <span className={styles.count}>
                {frozenCards?.length || 0} {t("TOTAL")}
                {searchTerm &&
                  t("ACCESS_CARD.HEADER_COUNT_SEARCH", {
                    count: filteredFrozenCards.length,
                  })}
              </span>
            </h2>
            <Table<FrozenCardTableRow>
              key="frozenCards"
              name="frozenCards"
              data={filteredFrozenCards}
              columns={frozenCardColumns}
              isLoading={isLoadingFrozenCards}
              noDataMessage={t("ACCESS_CARD.NO_FROZEN_CARDS")}
              onRowHover={handleFrozenCardRowHover}
              onRowClick={handleFrozenCardRowClick}
              enableColumnControls
              defaultSorting={[
                {
                  id: "updatedAt",
                  desc: true,
                },
              ]}
            />
          </div>
        </>
      ) : (
        <>
          <h2 className={`${styles.tableHeader} ${styles.completed}`}>
            {t("ACCESS_CARD.COMPLETED_ORDERS_HEADER")}
            <span className={styles.count}>
              {allCards?.length} {t("TOTAL")}
              {searchTerm &&
                t("ACCESS_CARD.HEADER_COUNT_SEARCH", {
                  count: filteredCards.length,
                })}
            </span>
          </h2>
          <Table
            key="accessCards"
            name="accessCards"
            isLoading={isLoadingAllCards}
            data={filteredCards}
            columns={restColumns}
            onRowClick={handleRowClick}
            noDataMessage={t("PAGES.BUILDING_ACCESS.ALL_PLACEHOLDER")}
            enableColumnControls
            defaultSorting={[
              {
                id: "activatedAt",
                desc: true,
              },
            ]}
          />
        </>
      )}
      {selectedCard && (
        <AccessCardDetailsModal
          open={modalOpen}
          buildingAdminMode
          accessCard={selectedCard}
          onClose={() => setModalOpen(false)}
        />
      )}
      <PopupCard
        onClose={() => setPrintCardsOpen(false)}
        wide
        nestedPopup
        open={printCardsOpen}
      >
        {isLoadingSelectedUsers ? (
          <>
            <h3>{t("ACCESS_CARD.ACTION_BUTTONS.PRINT_CARD")}</h3>
            <LoadingSpinner dark />
          </>
        ) : (
          <PrintAccessCard
            users={selectedUsers ?? []}
            isMultiCompany
            onClose={() => setPrintCardsOpen(false)}
            isLoadingUsers={isLoadingSelectedUsers}
          />
        )}
      </PopupCard>
      {selectedFrozenCard && (
        <FrozenCardDetailsModal
          open={frozenCardModalOpen}
          accessCard={selectedFrozenCard}
          onClose={() => setFrozenCardModalOpen(false)}
          userCompanyName={
            companyNames?.find(
              (c) => c.buildingTenantId === selectedFrozenCard.companyId,
            )?.buildingTenantName || "\u2014"
          }
        />
      )}
    </>
  );
}

function createBaseColumns(
  isCardOrders: boolean,
  allowBadgePrinting: boolean | undefined,
  t: (key: string) => string,
) {
  const columns = [];
  if (isCardOrders && allowBadgePrinting) {
    columns.push(
      columnHelper.accessor("selectCol", {
        cell: ({ row }) => {
          const handler = row.getToggleSelectedHandler();
          return (
            <Checkbox
              label="Select row"
              hideLabel
              checked={row.getIsSelected()}
              onChange={handler}
              onClick={(e) => e.stopPropagation()}
              style={{
                container: { display: "unset" },
                checkbox: { top: "unset" },
              }}
            />
          );
        },
        header: ({ table }) => (
          <Checkbox
            label="Select all"
            hideLabel
            checked={table.getIsAllPageRowsSelected()}
            nativeOnChange={table.getToggleAllPageRowsSelectedHandler()}
            style={{
              container: { display: "unset" },
              checkbox: { top: "unset" },
            }}
          />
        ),
        meta: { excludeFromColumnsToggler: true },
      }),
    );
  }
  columns.push(
    columnHelper.accessor("status", {
      cell: (info) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--wlcm-spacing-xs)",
          }}
        >
          <div
            aria-hidden
            style={{
              width: "1em",
              height: "1em",
              borderRadius: 100,
              backgroundColor: accessCardStatusColorMap[info.getValue()],
            }}
          />
          <span>
            {t(`ACCESS_CARD.STATUS.${info.getValue().toUpperCase()}.LABEL`)}
          </span>
        </div>
      ),
      header: () => <div>{t("ACCESS_CARD.TABLE_HEADERS.STATUS")}</div>,
      meta: { label: t("ACCESS_CARD.TABLE_HEADERS.STATUS") },
    }),
  );
  if (!isCardOrders) {
    columns.push(
      columnHelper.accessor("cardNumber", {
        cell: (info) => info.getValue() || "\u2014",
        header: () => <div>{t("ACCESS_CARD.TABLE_HEADERS.CARD_NUMBER")}</div>,
        meta: { label: t("ACCESS_CARD.TABLE_HEADERS.CARD_NUMBER") },
      }),
    );
  }
  if (isCardOrders) {
    columns.push(
      columnHelper.accessor("createdAt", {
        cell: (info) => {
          const timestamp = info.getValue();
          return timestamp
            ? formatTimestampToTimeAgo(t, timestamp, i18n.language, true)
            : "\u2014";
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("ACCESS_CARD.TABLE_HEADERS.ORDERED_AT")}
          />
        ),
        sortingFn: timestampSortingFn,
        sortUndefined: -1,
        meta: { label: t("ACCESS_CARD.TABLE_HEADERS.ORDERED_AT") },
      }),
    );
  } else {
    columns.push(
      columnHelper.accessor("activatedAt", {
        cell: (info) => {
          const timestamp = info.getValue();
          return timestamp
            ? formatTimestampToTimeAgo(t, timestamp, i18n.language, true)
            : "\u2014";
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("ACCESS_CARD.TABLE_HEADERS.ACTIVATED_AT")}
          />
        ),
        sortingFn: timestampSortingFn,
        sortUndefined: -1,
        meta: { label: t("ACCESS_CARD.TABLE_HEADERS.ACTIVATED_AT") },
      }),
    );
  }
  columns.push(
    columnHelper.accessor("orderedBy.company", {
      cell: (info) => info.getValue(),
      header: () => <div>{t("ACCESS_CARD.TABLE_HEADERS.COMPANY")}</div>,
      meta: { label: t("ACCESS_CARD.TABLE_HEADERS.COMPANY") },
    }),
    columnHelper.accessor("orderedBy", {
      cell: (info) => {
        const orderedBy = info.getValue();
        return `${orderedBy?.name || "\u2014"} ${orderedBy?.email ? "(" + orderedBy.email + ")" : ""}`;
      },
      header: () => <div>{t("ACCESS_CARD.TABLE_HEADERS.ORDERED_BY")}</div>,
      meta: { label: t("ACCESS_CARD.TABLE_HEADERS.ORDERED_BY") },
    }),
    columnHelper.accessor("userName", {
      cell: (info) => info.getValue() || "\u2014",
      header: (info) => (
        <SortableTableHeader
          info={info}
          label={t("ACCESS_CARD.TABLE_HEADERS.RECIPIENT")}
        />
      ),
      meta: { label: t("ACCESS_CARD.TABLE_HEADERS.RECIPIENT") },
    }),
    columnHelper.accessor("type", {
      cell: (info) =>
        info.getValue() === CardType.PHYSICAL ? (
          <>
            <Icon name="badge" className="physical-card-icon" />
            <Tooltip
              anchorSelect=".physical-card-icon"
              content={t("ACCESS_CARD.ARIA_DESCRIPTION.PHYSICAL")}
            />
          </>
        ) : (
          <>
            <Icon name="phone_android" className="digital-card-icon" />
            <Tooltip
              anchorSelect=".digital-card-icon"
              content={t("ACCESS_CARD.ARIA_DESCRIPTION.DIGITAL")}
            />
          </>
        ),
      header: () => <div>Type</div>,
      meta: { label: "Type" },
    }),
  );

  return columns;
}

function createFrozenCardColumns(t: (key: string) => string) {
  return [
    frozenColumnHelper.accessor("state", {
      cell: (info) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--wlcm-spacing-xs)",
          }}
        >
          <div
            aria-hidden
            style={{
              width: "1em",
              height: "1em",
              borderRadius: 100,
              backgroundColor: accessCardStatusColorMapNew[info.getValue()],
            }}
          />
          <span>
            {t(`ACCESS_CARD.STATUS.${info.getValue().toUpperCase()}.LABEL`)}
          </span>
        </div>
      ),
      header: () => <div>{t("ACCESS_CARD.TABLE_HEADERS.STATUS")}</div>,
      meta: { label: t("ACCESS_CARD.TABLE_HEADERS.STATUS") },
    }),
    frozenColumnHelper.accessor("cardNumber", {
      cell: (info) => info.getValue() || "\u2014",
      header: () => <div>{t("ACCESS_CARD.TABLE_HEADERS.CARD_NUMBER")}</div>,
      meta: { label: t("ACCESS_CARD.TABLE_HEADERS.CARD_NUMBER") },
    }),
    frozenColumnHelper.accessor("updatedAt", {
      cell: (info) => {
        const time = info.getValue();
        if (!time) return "\u2014";
        const dateTime = new Date(time);
        const timestamp = dateToFirebaseTimestamp(dateTime);

        return timestamp
          ? formatTimestampToTimeAgo(t, timestamp, i18n.language, true)
          : "\u2014";
      },
      header: (info) => (
        <SortableTableHeader
          info={info}
          label={t("ACCESS_CARD.TABLE_HEADERS.FROZEN_AT")}
        />
      ),
      sortingFn: dateLikeSortingFn,
      meta: { label: t("ACCESS_CARD.TABLE_HEADERS.FROZEN_AT") },
    }),
    frozenColumnHelper.accessor("userName", {
      cell: (info) => info.getValue() || "\u2014",
      header: (info) => (
        <SortableTableHeader
          info={info}
          label={t("ACCESS_CARD.TABLE_HEADERS.CARD_HOLDER")}
        />
      ),
      meta: { label: t("ACCESS_CARD.TABLE_HEADERS.CARD_HOLDER") },
    }),
    frozenColumnHelper.accessor("type", {
      cell: (info) =>
        info.getValue() === CardTypeNew.PLASTIC ? (
          <>
            <Icon name="badge" className="physical-card-icon" />
            <Tooltip
              anchorSelect=".physical-card-icon"
              content={t("ACCESS_CARD.ARIA_DESCRIPTION.PHYSICAL")}
            />
          </>
        ) : (
          <>
            <Icon name="phone_android" className="digital-card-icon" />
            <Tooltip
              anchorSelect=".digital-card-icon"
              content={t("ACCESS_CARD.ARIA_DESCRIPTION.DIGITAL")}
            />
          </>
        ),
      header: () => <div>Type</div>,
      meta: { label: "Type" },
    }),
    frozenColumnHelper.accessor("companyName", {
      cell: (info) => info.getValue() || "\u2014",
      header: () => <div>{t("ACCESS_CARD.TABLE_HEADERS.COMPANY")}</div>,
      meta: { label: t("ACCESS_CARD.TABLE_HEADERS.COMPANY") },
    }),
  ];
}
